// ═══════════════════════════════════════════════════════════════
// SHELTER MANAGEMENT — Supply Inventory
// Track consumables, highlight low stock, quick quantity updates
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid, NumberInput,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconMinus,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useShelterStore } from '../../store/shelterStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { SUPPLY_CATEGORIES } from '../../types/shelter';
import type { ShelterSupply, SupplyCategory } from '../../types/shelter';

const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'case', label: 'Case' },
  { value: 'pallet', label: 'Pallet' },
  { value: 'box', label: 'Box' },
];

const CATEGORY_COLORS: Record<SupplyCategory, string> = {
  bedding: 'blue', food: 'green', water: 'cyan', hygiene: 'grape',
  medical: 'red', equipment: 'orange', other: 'gray',
};

const EMPTY: Partial<ShelterSupply> = {
  shelterId: '', itemName: '', category: 'other', quantity: 0, unit: 'each',
  reorderThreshold: 0, notes: '',
};

export default function Supplies() {
  const { canEdit } = useModulePermission('shelter');
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const {
    supplies, shelters,
    loadingSupplies,
    loadSupplies, loadShelters,
    createSupplyItem, updateSupplyItem, deleteSupplyItem,
  } = useShelterStore();

  const [search, setSearch] = useState('');
  const [shelterFilter, setShelterFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ShelterSupply | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShelterSupply | null>(null);
  const [form, setForm] = useState<Partial<ShelterSupply>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSupplies(); loadShelters(); }, [loadSupplies, loadShelters]);

  // Store already server-side filters by active incident; use directly
  const iSupplies = incidentId ? supplies : [];
  const iShelters = incidentId ? shelters : [];

  const shelterMap = useMemo(() => {
    const m = new Map<string, string>();
    iShelters.forEach(s => m.set(String(s.Rid), s.name));
    return m;
  }, [iShelters]);

  const shelterOptions = useMemo(() =>
    iShelters.map(s => ({ value: String(s.Rid), label: s.name })),
    [iShelters],
  );

  const shelterFilterOptions = useMemo(() =>
    [{ value: 'all', label: 'All Shelters' }, ...shelterOptions],
    [shelterOptions],
  );

  const categoryOptions = useMemo(() =>
    SUPPLY_CATEGORIES.map(c => ({ value: c.value, label: c.label })),
    [],
  );

  const filtered = useMemo(() => {
    let list = iSupplies;
    if (shelterFilter && shelterFilter !== 'all') list = list.filter(s => s.shelterId === shelterFilter);
    if (categoryFilter && categoryFilter !== 'all') list = list.filter(s => s.category === categoryFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        s.itemName.toLowerCase().includes(q) ||
        (shelterMap.get(s.shelterId) ?? '').toLowerCase().includes(q),
      );
    }
    return [...list].sort((a, b) => a.itemName.localeCompare(b.itemName));
  }, [iSupplies, shelterFilter, categoryFilter, search, shelterMap]);

  const setField = (key: keyof ShelterSupply, value: unknown) => setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY }); setModalOpen(true); };
  const openEdit = (s: ShelterSupply) => { setEditTarget(s); setForm({ ...s }); setModalOpen(true); };

  const handleSave = async () => {
    if (!form.itemName?.trim()) {
      notifications.show({ message: 'Item name is required', color: 'yellow' });
      return;
    }
    if (!form.shelterId) {
      notifications.show({ message: 'Shelter is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<ShelterSupply> = {
        ...form,
        incidentId,
        quantity: Number(form.quantity) || 0,
        reorderThreshold: Number(form.reorderThreshold) || 0,
      };

      if (editTarget) {
        await updateSupplyItem(editTarget.Rid, payload);
        notifications.show({ message: 'Supply item updated', color: 'green' });
      } else {
        payload.lastRestocked = new Date().toISOString();
        await createSupplyItem(payload);
        notifications.show({ message: 'Supply item created', color: 'green' });
      }
      setModalOpen(false);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally { setSaving(false); }
  };

  const handleQuickUpdate = async (item: ShelterSupply, delta: number) => {
    const newQty = Math.max(0, item.quantity + delta);
    try {
      await updateSupplyItem(item.Rid, {
        quantity: newQty,
        lastRestocked: delta > 0 ? new Date().toISOString() : item.lastRestocked,
      });
      notifications.show({
        message: `${item.itemName}: ${item.quantity} -> ${newQty}`,
        color: newQty <= item.reorderThreshold ? 'orange' : 'green',
      });
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Update failed', color: 'red' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteSupplyItem(deleteTarget.Rid);
      notifications.show({ message: `${deleteTarget.itemName} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Supply Inventory</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingSupplies} onClick={() => loadSupplies()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Supply Item</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm" wrap="wrap">
        <TextInput placeholder="Search item name, shelter..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={shelterFilterOptions}
          value={shelterFilter ?? 'all'} onChange={v => setShelterFilter(v)} size="xs" w={180}
          placeholder="Filter by shelter" />
        <Select data={[{ value: 'all', label: 'All Categories' }, ...categoryOptions]}
          value={categoryFilter ?? 'all'} onChange={v => setCategoryFilter(v)} size="xs" w={180} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} item{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto', maxHeight: 500, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Item Name</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Category</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Quantity</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Unit</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Reorder At</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Last Restocked</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Shelter</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Quick Adj.</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                  {loadingSupplies ? 'Loading...' : 'No supply items found'}
                </td></tr>
              )}
              {filtered.map(s => {
                const isLow = s.reorderThreshold > 0 && s.quantity <= s.reorderThreshold;
                return (
                  <tr key={s.id}
                    style={{
                      borderBottom: '1px solid var(--mantine-color-gray-2)',
                      backgroundColor: isLow ? 'var(--mantine-color-red-0)' : undefined,
                    }}>
                    <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                      {s.itemName || '-'}
                      {isLow && <Badge size="xs" color="red" ml={6}>LOW</Badge>}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Badge size="sm" color={CATEGORY_COLORS[s.category] ?? 'gray'}>
                        {s.category}
                      </Badge>
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                      <Text size="sm" c={isLow ? 'red' : undefined}>{s.quantity}</Text>
                    </td>
                    <td style={{ padding: '8px 12px' }}>{s.unit}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {s.reorderThreshold > 0 ? s.reorderThreshold : '-'}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <Text size="sm" c="dimmed">
                        {s.lastRestocked ? new Date(s.lastRestocked).toLocaleDateString() : '-'}
                      </Text>
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {(shelterMap.get(s.shelterId) ?? s.shelterId) || '-'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {canEdit && (
                      <Group gap={2} justify="center">
                        <ActionIcon size="xs" variant="light" color="red"
                          onClick={() => handleQuickUpdate(s, -1)} disabled={s.quantity <= 0}>
                          <IconMinus size={10} />
                        </ActionIcon>
                        <ActionIcon size="xs" variant="light" color="green"
                          onClick={() => handleQuickUpdate(s, 1)}>
                          <IconPlus size={10} />
                        </ActionIcon>
                        <ActionIcon size="xs" variant="light" color="blue"
                          onClick={() => handleQuickUpdate(s, 10)} title="+10">
                          <Text size="xs" fw={700}>+10</Text>
                        </ActionIcon>
                      </Group>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                      {canEdit && (
                      <Group gap={4} justify="flex-end">
                        <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(s)}>
                          <IconPencil size={14} />
                        </ActionIcon>
                        <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(s)}>
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Supply Item' : 'Add Supply Item'} centered size="md">
        <Stack>
          <Select label="Shelter" required searchable
            data={shelterOptions} value={form.shelterId ?? ''}
            onChange={v => setField('shelterId', v ?? '')}
            placeholder="Select shelter" />

          <TextInput label="Item Name" required value={form.itemName ?? ''}
            onChange={e => setField('itemName', e.currentTarget.value)}
            placeholder="e.g. Blankets, Water Bottles, Cots" />

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Category" data={categoryOptions}
              value={form.category ?? 'other'}
              onChange={v => setField('category', v ?? 'other')} />
            <Select label="Unit" data={UNIT_OPTIONS}
              value={form.unit ?? 'each'}
              onChange={v => setField('unit', v ?? 'each')} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <NumberInput label="Quantity" min={0}
              value={form.quantity ?? 0}
              onChange={v => setField('quantity', v)} />
            <NumberInput label="Reorder Threshold" min={0}
              value={form.reorderThreshold ?? 0}
              onChange={v => setField('reorderThreshold', v)} />
          </SimpleGrid>

          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Add Item'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Supply Item" centered size="sm">
        <Stack>
          <Text size="sm">Delete <strong>{deleteTarget?.itemName}</strong>? This cannot be undone.</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
